import { createPinia, setActivePinia, Pinia } from 'pinia';
import { useAuthStore } from '@/modules/auth/store';
import { useNavStore } from '@/modules/core/nav/store';
import { useUserStore } from '@/modules/user/store';
import { useUiStore } from '@/modules/core/ui/store';
import { useAccountingStore } from '@/modules/accounting/store';
import { useTeamStore } from '@/modules/team/store';
import { usePredefinedStore } from '@/modules/predefined/store';
import { useNotificationStore } from '@/modules/notification/store';
import { useMeetingStore } from '@/modules/meeting/store';
import { useInboxStore } from '@/modules/inbox/store';
import { useFinancialAccountStore } from '@/modules/financialaccount/store';
import { useExportStore } from '@/modules/export/store';
import { useEstateAccountingQuebecDistributionStore } from '@/modules/estateaccountingquebecdistribution/store';
import { useBillingStore } from '@/modules/billing/store';
import { useEstateStore } from '@/modules/estate/store';
import { useEsignStore } from '@/modules/esign/store';
import { useApiStore } from '@/modules/core/api/store';
import { useAnnouncementStore } from '@/modules/announcement/store';

class StoreHelper {
  private _stores = new Map<string, unknown>();
  private _pinia: Pinia | null = null;

  init(): Pinia {
    if (!this._pinia) {
      this._pinia = createPinia();
      setActivePinia(this._pinia);
    }

    return this._pinia;
  }

  getPinia(): Pinia {
    return this.init();
  }

  private getStore<T>(key: string, factory: () => T): T {
    this.init();

    if (!this._stores.has(key)) {
      this._stores.set(key, factory());
    }
    return this._stores.get(key) as T;
  }

  get auth(): ReturnType<typeof useAuthStore> {
    return this.getStore('auth', () => useAuthStore());
  }

  get nav(): ReturnType<typeof useNavStore> {
    return this.getStore('nav', () => useNavStore());
  }

  get user(): ReturnType<typeof useUserStore> {
    return this.getStore('user', () => useUserStore());
  }

  get ui(): ReturnType<typeof useUiStore> {
    return this.getStore('ui', () => useUiStore());
  }

  get accounting(): ReturnType<typeof useAccountingStore> {
    return this.getStore('accounting', () => useAccountingStore());
  }

  get team(): ReturnType<typeof useTeamStore> {
    return this.getStore('team', () => useTeamStore());
  }

  get predefined(): ReturnType<typeof usePredefinedStore> {
    return this.getStore('predefined', () => usePredefinedStore());
  }

  get notification(): ReturnType<typeof useNotificationStore> {
    return this.getStore('notification', () => useNotificationStore());
  }

  get meeting(): ReturnType<typeof useMeetingStore> {
    return this.getStore('meeting', () => useMeetingStore());
  }

  get inbox(): ReturnType<typeof useInboxStore> {
    return this.getStore('inbox', () => useInboxStore());
  }

  get financialaccount(): ReturnType<typeof useFinancialAccountStore> {
    return this.getStore('financialaccount', () => useFinancialAccountStore());
  }

  // Using bracket notation to avoid reserved keyword
  get ['export'](): ReturnType<typeof useExportStore> {
    return this.getStore('export', () => useExportStore());
  }

  get estateaccountingquebecdistribution(): ReturnType<
    typeof useEstateAccountingQuebecDistributionStore
  > {
    return this.getStore('estateaccountingquebecdistribution', () =>
      useEstateAccountingQuebecDistributionStore()
    );
  }

  get billing(): ReturnType<typeof useBillingStore> {
    return this.getStore('billing', () => useBillingStore());
  }

  get estate(): ReturnType<typeof useEstateStore> {
    return this.getStore('estate', () => useEstateStore());
  }

  get esign(): ReturnType<typeof useEsignStore> {
    return this.getStore('esign', () => useEsignStore());
  }

  get api(): ReturnType<typeof useApiStore> {
    return this.getStore('api', () => useApiStore());
  }

  get announcement(): ReturnType<typeof useAnnouncementStore> {
    return this.getStore('announcement', () => useAnnouncementStore());
  }
}

export default new StoreHelper();
